import { createApi } from '@reduxjs/toolkit/query/react'
import { LocalStorage } from 'ts-localstorage'
import baseQueryWithReauth from '../baseQueryWithReauth'
import { Conversation, ConversationStats, GetConversationResponse } from '../../types/conversation.type'
import { ACCESS_TOKEN } from '../../config/consts'

export const conversationApi = createApi({
  reducerPath: 'conversationApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Conversation'],
  endpoints: (builder) => ({
    getConversationList: builder.query<GetConversationResponse, { agentId?: string; pageSize?: number; page?: number }>(
      {
        query: ({ agentId, pageSize, page }) => {
          const queryParams = new URLSearchParams()
          if (agentId) {
            queryParams.append('agentId', agentId)
          }
          if (pageSize) {
            queryParams.append('pageSize', pageSize.toString())
          }
          if (page) {
            queryParams.append('page', page.toString())
          }
          return {
            url: `conversations?${queryParams.toString()}`,
            method: 'GET',
            credentials: 'include',
            mode: 'cors',
            headers: {
              authorization: 'Bearer ' + (LocalStorage.getItem(ACCESS_TOKEN) as string),
            },
          }
        },
        providesTags: (result) =>
          result
            ? [
                ...result.conversations.map(({ id }) => ({ type: 'Conversation' as const, id })),
                { type: 'Conversation' as const, id: 'LIST' },
              ]
            : [{ type: 'Conversation' as const, id: 'LIST' }],
      },
    ),
    getConversationStats: builder.query<ConversationStats, void>({
      query: () => ({
        url: `conversations/stats`,
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers: {
          authorization: 'Bearer ' + (LocalStorage.getItem(ACCESS_TOKEN) as string),
        },
      }),
    }),
    getConversationById: builder.query<Conversation, { id: string }>({
      query: ({ id }) => ({
        url: `conversations/${id}`,
        method: 'GET',
        credentials: 'include',
        mode: 'cors',
        headers: {
          authorization: 'Bearer ' + (LocalStorage.getItem(ACCESS_TOKEN) as string),
        },
      }),
      providesTags: (result, error, arg) => [{ type: 'Conversation' as const, id: arg.id }],
    }),
    deleteConversation: builder.mutation<void, string>({
      query: (id) => ({
        url: `conversations/${id}`,
        method: 'DELETE',
        credentials: 'include',
        mode: 'cors',
        headers: {
          authorization: 'Bearer ' + (LocalStorage.getItem(ACCESS_TOKEN) as string),
        },
      }),
      invalidatesTags: [{ type: 'Conversation' as const, id: 'LIST' }],
    }),
    updateConversation: builder.mutation<Conversation, { id: string; changes: Partial<Conversation> }>({
      query: ({ id, changes }) => ({
        url: `conversations/${id}`,
        method: 'PUT',
        body: changes,
        credentials: 'include',
        mode: 'cors',
        headers: {
          authorization: 'Bearer ' + (LocalStorage.getItem(ACCESS_TOKEN) as string),
        },
      }),
      invalidatesTags: [{ type: 'Conversation' as const, id: 'LIST' }],
    }),
  }),
})

export const {
  useGetConversationListQuery,
  useLazyGetConversationListQuery,
  useGetConversationStatsQuery,
  useLazyGetConversationByIdQuery,
  useGetConversationByIdQuery,
  useDeleteConversationMutation,
  useUpdateConversationMutation,
} = conversationApi
